import {
  type ForwardedRef,
  type ElementType,
  type ComponentPropsWithRef,
} from "react";
import { CoreTypography } from "@boi/components-core";
import { forwardRefGenerics } from "@boi/react-utils";

import { TypographyBase, type TypographyBaseProps } from "./TypographyBase";
import { TYPOGRAPHY_VARIANT_TO_TAG_MAP } from "./Typography.constants";

type BaseProps = Omit<
  TypographyBaseProps,
  "variant" | "fontFamily" | "component" | "children"
>;

type HeadingVariantProps = BaseProps & {
  variant?: CoreTypography.HeadingVariant;
};

type StandardVariantProps = BaseProps & {
  variant?: CoreTypography.StandardVariant;
  fontFamily?: CoreTypography.FontFamily;
};

type DistributiveOmit<T, TOmitted extends PropertyKey> = T extends any
  ? Omit<T, TOmitted>
  : never;

export type TypographyProps<T extends ElementType> = (
  | HeadingVariantProps
  | StandardVariantProps
) & {
  component?: T;
} & DistributiveOmit<
    ComponentPropsWithRef<ElementType extends T ? "p" : T>,
    "component"
  >;

function UnwrappedTypography<T extends ElementType>(
  props: TypographyProps<T>,
  ref: ForwardedRef<any>
) {
  const {
    variant = CoreTypography.DEFAULT_VARIANT,
    component,
    ...otherProps
  } = props;

  return (
    <TypographyBase
      {...(otherProps as any)}
      ref={ref}
      variant={variant}
      component={component || TYPOGRAPHY_VARIANT_TO_TAG_MAP[variant]}
    />
  );
}

export const Typography = forwardRefGenerics(UnwrappedTypography);
